import { Injectable } from '@angular/core';

import { Actions, Effect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, tap, take } from 'rxjs/operators';
import { of } from 'rxjs';

import { FilterDataUserResponse, UtentiService } from '../utenti.service';
import * as utentiAction from './utenti.actions';
import { AuthService } from 'src/app/auth/auth.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { UpdateLoggedUserSuccess, Logout } from 'src/app/auth/store';
import { Store } from '@ngrx/store';
import { AppState, getMyUtenti } from 'src/app/store/app.state';
import { FilterData } from 'src/app/shared/models/filter-data/filter-data.model';

/**Classe che gestisce i side effect per la sezione utenti dello store */
@Injectable()
export class UtentiEffects {
    /**
     * Costruttore per UtentiEffects
     * @param actions$ {Actions}
     * @param utentiService {UtentiService} servizi che gestiscono chiamate a BE per la sezione utenti
     * @param authService {AuthService} servizi che gestiscono la sezione di autenticazione
     * @param utility {UtilityService} servizio che gestisce una serie di funzionalità di uso comune
     */
    constructor(
        private actions$: Actions,
        private utentiService: UtentiService,
        private authService: AuthService,
        private utility: UtilityService,
        private store: Store<AppState>
    ) { }

    /**Side effect per l'azione di inizio recupero elenco utenti */
    @Effect()
    startGetUtenti = this.actions$.pipe(
        ofType(utentiAction.START_GET_USER_LIST),
        switchMap((action: utentiAction.StartGetUserList) => {
            let bodyPost = {};
            this.store.select(getMyUtenti).pipe(take(1)).subscribe(data => {
                bodyPost = <FilterData>{
                    filter: data.elencoFiltriUtenti ? data.elencoFiltriUtenti : null,
                    fromRow: null,
                    toRow: null,
                    orderBy: data.ordinamentoUtenti ? data.ordinamentoUtenti : [],
                    reload: true
                };

            });

            return this.utentiService.getListaUtenti(bodyPost).pipe(
                map((resData: FilterDataUserResponse) => new utentiAction.GetUserListSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getAccountList.' + errRes.error));
                    }
                    return of(new utentiAction.GetUserListFailure(errRes));
                })
            )
        })
    );

    /**Side effect per l'avvenuto recupero dell'elenco utenti */
    @Effect({ dispatch: false })
    getUtentiSuccess = this.actions$.pipe(
        ofType(utentiAction.GET_USER_LIST_SUCCESS),
        tap(() => this.authService.refreshLogoutTimer())
    );

    /**Side effect per l'inizio dell'azione di creazione di un nuovo utente */
    @Effect()
    startCreateUser = this.actions$.pipe(
        ofType(utentiAction.START_CREATE_USER),
        switchMap((action: utentiAction.StartCreateUser) =>
            this.utentiService.createNewUser(action.payload).pipe(
                switchMap(resData => {
                    return [
                        new utentiAction.CreateUserSuccess(resData, action.isUndoRedoOperation)
                    ];
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('createUser.' + errRes.error));
                    }
                    return of(new utentiAction.CreateUserFailure(errRes));
                })
            )
        )
    );

    /**Side effect per l'avvenuta creazione di un nuovo utente */
    @Effect({ dispatch: false })
    createUserSuccess = this.actions$.pipe(
        ofType(utentiAction.CREATE_USER_SUCCESS),
        tap(() => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString("utentiComponentString.addedUser"));
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'inizio dell'azione di rimozione utente */
    @Effect()
    startRemoveUser = this.actions$.pipe(
        ofType(utentiAction.START_REMOVE_USER),
        switchMap((action: utentiAction.StartRemoveUser) =>
            this.utentiService.removeUser(action.payload).pipe(
                map(resData => {
                    console.log(resData);
                    return new utentiAction.RemoveUserSuccess(action.payload, action.isUndoRedoOperation);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('removeUser.' + errRes.error));
                    }
                    return of(new utentiAction.RemoveUserFailure(errRes));
                })
            )
        )
    );

    /**Side effect per l'avvenuta rimozione di un utente */
    @Effect({ dispatch: false })
    removeUserSuccess = this.actions$.pipe(
        ofType(utentiAction.REMOVE_USER_SUCCESS),
        tap(() => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString("utentiComponentString.removedUser"));
            this.authService.refreshLogoutTimer();
        })
    );

    @Effect()
    startRestoreUser = this.actions$.pipe(
        ofType(utentiAction.START_RESTORE_USER),
        switchMap((action: utentiAction.StartRestoreUser) =>
            this.utentiService.restoreUser(action.payload).pipe(
                switchMap(resData => {
                    return [
                        new utentiAction.RestoreUserSuccess(),
                        new utentiAction.StartGetUserList()
                    ];
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('restoreUser.' + errRes.error));
                    }
                    return of(new utentiAction.RestoreUserFailure());
                })
            )
        )
    );

    /**Side effect per l'avvenuta rimozione di un utente */
    @Effect({ dispatch: false })
    restoreUserSuccess = this.actions$.pipe(
        ofType(utentiAction.RESTORE_USER_SUCCESS),
        tap(() => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString("utentiComponentString.restoreUser"));
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'inizio di modifica utente */
    @Effect()
    startupdateUserV2 = this.actions$.pipe(
        ofType(utentiAction.START_UPDATE_V2_USER),
        switchMap((action: utentiAction.StartUpdateUserV2) => {
            return this.utentiService.updateUserV2(action.payload.idUser, action.payload.nomeCampo, action.payload.newValue).pipe(
                switchMap(resData => {
                    if (resData.id === JSON.parse(localStorage.getItem('loggedUser')).id) { //nel caso in cui si sia modificato l'utente loggato, lancio anche l'azione di successo per la modifica dell'utente loggato così da aggiornare l'auth e il localStorage
                        if (action.payload.nomeCampo === 'enabled') {
                            return [new Logout()];
                        }
                        return [
                            new utentiAction.UpdateUserV2Success({ nuovoUtente: resData, campoMod: action.payload.nomeCampo }, action.isUndoRedoOperation),
                            new UpdateLoggedUserSuccess(resData)
                        ]
                    } else {
                        return [new utentiAction.UpdateUserV2Success({ nuovoUtente: resData, campoMod: action.payload.nomeCampo }, action.isUndoRedoOperation)];
                    }
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('updateUser.' + errRes.error));
                    }
                    return of(new utentiAction.UpdateUserV2Failure(errRes));
                })
            )
        })
    );

    /**Side effect per l'avvenuta modifica di un utente */
    @Effect({ dispatch: false })
    updateUserSuccessV2 = this.actions$.pipe(
        ofType(utentiAction.UPDATE_USER_V2_SUCCESS),
        map((action: utentiAction.UpdateUserV2Success) => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString("utentiComponentString.updatedUser"));
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio recupero risorse (lancia il servizio che gestisce la chiamata GET) */
    @Effect()
    startGetResources = this.actions$.pipe(
        ofType(utentiAction.START_GET_RESOURCES),
        switchMap((action: utentiAction.StartGetResources) =>
            this.utentiService.getUserResources(action.paylaod).pipe(
                map(resData => {
                    return new utentiAction.GetResourcesSuccess(resData);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getAccountResources.' + errRes.error));
                    }
                    return of(new utentiAction.GetResourcesFailure(errRes));
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto recupero risorse utente */
    @Effect({ dispatch: false })
    getResourcesSuccess = this.actions$.pipe(
        ofType(utentiAction.GET_RESOURCES_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio recupero elenco trattati */
    @Effect()
    startGetTractates = this.actions$.pipe(
        ofType(utentiAction.START_GET_TRACTATES),
        switchMap((action: utentiAction.StartGetTractates) =>
            this.utentiService.getTractatesList().pipe(
                map(resData => {
                    return new utentiAction.GetTractatesSuccess(resData);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getTractates.' + errRes.error));
                    }
                    return of(new utentiAction.GetTractatesFailure(errRes));
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto recupero elenco trattati */
    @Effect({ dispatch: false })
    getTractatesSuccess = this.actions$.pipe(
        ofType(utentiAction.GET_TRACTATES_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'inizio dell'azione di recupero sotto-risorse (capitoli, etc) di una risorsa */
    @Effect()
    startGetSubResources = this.actions$.pipe(
        ofType(utentiAction.START_GET_SUB_RESOURCES),
        switchMap((action: utentiAction.StartGetSubResources) =>
            this.utentiService.getSubResourcesList(action.payload).pipe(
                map(resData => {
                    return new utentiAction.GetSubResourcesSuccess(resData);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getSubResources.' + errRes.error));
                    }
                    return of(new utentiAction.GetSubResourcesFailure(errRes));
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto recupero delle sotto-risorse di una risorsa */
    @Effect({ dispatch: false })
    getSubResourcesSuccess = this.actions$.pipe(
        ofType(utentiAction.GET_SUB_RESOURCES_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'inizio dell'azione di aggiunta risorsa assegnata a un utente */
    @Effect()
    startAddUserResource = this.actions$.pipe(
        ofType(utentiAction.START_ADD_USER_RESOURCE),
        switchMap((action: utentiAction.StartAddUserResource) =>
            this.utentiService.addResourceToUser(action.payload.userId, action.payload.resource.id).pipe(
                map(resData => {
                    return new utentiAction.AddUserResourceSuccess(action.payload.resource);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('addUserResource.' + errRes.error));
                    }
                    return of(new utentiAction.AddUserResourceFailure(errRes));
                })
            )
        )
    );

    /**Side effect per l'azione di aggiunta risorsa assegnata a un utente */
    @Effect({ dispatch: false })
    addUserResourceSuccess = this.actions$.pipe(
        ofType(utentiAction.ADD_USER_RESOURCE_SUCCESS),
        tap(() => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString('addUserResource.resourceAdded'));
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'inizio dell'azione di rimozione risorsa precedente assegnata a un utente */
    @Effect()
    startRemoveUserResource = this.actions$.pipe(
        ofType(utentiAction.START_REMOVE_USER_RESOURCE),
        switchMap((action: utentiAction.StartRemoveUserResource) =>
            this.utentiService.removeResourceFromUser(action.payload.userId, action.payload.resourceId).pipe(
                map(resData => {
                    return new utentiAction.RemoveUserResourceSuccess(action.payload.resourceId);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('removeUserResource.' + errRes.error));
                    }
                    return of(new utentiAction.RemoveUserResourceFailure(errRes));
                })
            )
        )
    );

    /**Side effect per l'avvenuta rimozione di una risorsa precedentemente assegnata a un utente */
    @Effect({ dispatch: false })
    removeUserResourceSuccess = this.actions$.pipe(
        ofType(utentiAction.REMOVE_USER_RESOURCE_SUCCESS),
        tap(() => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString('removeUserResource.resourceRemoved'))
            this.authService.refreshLogoutTimer();
        })
    );

}